import { useContext, useMemo } from 'react';
import { Title, useMantineTheme } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { FlexExtended } from '@dealstack/frontend/shared/components/Flex/FlexExtended';
import { PageContainer } from '@dealstack/frontend/shared/layout/PageContainer/PageContainer.tsx';
import { QuickSwitchBreadcrumbsNav } from '@dealstack/frontend/shared/components/QuickSwitchBreadcrumbsNav/QuickSwitchBreadcrumbsNav.tsx';
import { makeDealPageBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/dealBreadcrumbs.utils.tsx';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
	useTranslationMipMessages,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { DetailedMessage } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/DetailedMessage/DetailedMessage';
import { useDealEventMessage } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventMessage.ts';
import {
	dealEventLogPath,
	dealEventMessagePath,
	readEventLogSearch,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/dealEventLog.utils.ts';

export const DealEventMessage = () => {
	const tCapManage = useTranslationCapManage();
	const tCommonUi = useTranslationCommonUi();
	const tMipMessages = useTranslationMipMessages();
	const theme = useMantineTheme();
	const { dealId, messageId } = useRequiredParams('dealId', 'messageId');
	const { state } = useLocation();
	const { deal, deals } = useContext(DealContext);
	const { data: message, isFetching } = useDealEventMessage(dealId, messageId);

	const title = tMipMessages('page.header');

	const breadcrumbs = useMemo(
		() => [
			...makeDealPageBreadcrumbs({ tCommonUi, dealId, dealName: deal?.name, deals }),
			{
				name: tCapManage('deal_event_log.title'),
				url: dealEventLogPath(dealId, readEventLogSearch(state)),
			},
			{
				name: message?.subject ?? title,
				url: dealEventMessagePath(dealId, messageId),
			},
		],
		[
			tCommonUi,
			tCapManage,
			dealId,
			deal?.name,
			deals,
			state,
			message?.subject,
			messageId,
			title,
		]
	);

	useBreadcrumbs(breadcrumbs, { skipFirst: true });

	return (
		<PageContainer>
			<QuickSwitchBreadcrumbsNav />

			<Title fz={32} fw={600} lh={1} c="dark.8">
				{title}
			</Title>

			<FlexExtended
				direction="row"
				border={`1px solid ${theme.colors.gray[4]}`}
				mih="calc(100vh - 13rem)"
			>
				<DetailedMessage message={message} isLoading={isFetching} />
			</FlexExtended>
		</PageContainer>
	);
};
